import { config } from 'config';
import { RestApiMethodName } from 'lib/enum/rest-api-method';
import { HttpResponseError } from 'lib/error/http-response-error';
import { AbortError } from 'p-retry';
import queryString from 'query-string';
import { mergeDeepLeft } from 'ramda';

const STATUS_418_CODE = 418;

export const createHttpResponseError = (
    errorMessage: string,
    response?: Response,
    resolvedResponseBody?: any,
): HttpResponseError => {
    try {
        const error = new HttpResponseError(errorMessage);
        error.response = response;
        error.resolvedResponseBody = resolvedResponseBody;
        return error;
    } catch (e) {
        const error = new HttpResponseError('Unknown error occurred');
        error.response = response;
        error.resolvedResponseBody = resolvedResponseBody;
        return error;
    }
};

export const checkStatus = async (response: Response): Promise<Response> => {
    if (response.ok) {
        return response;
    }
    try {
        const jsonResponse = await response.json();
        if (response.status === 401) {
            throw createHttpResponseError(`${response.status} ${response.statusText}`, response, jsonResponse);
        } else if (response.status === 400 || response.status === 422) {
            return await Promise.reject(
                createHttpResponseError(`${response.status} ${response.statusText}`, response, jsonResponse),
            );
        }
        const resolvedResponseBody =
            Object.entries(jsonResponse || {}).length && response.status === STATUS_418_CODE ? jsonResponse : {};

        return await Promise.reject(
            createHttpResponseError(`${response.status} ${response.statusText}`, response, resolvedResponseBody),
        );
    } catch (error: any) {
        return await Promise.reject(
            createHttpResponseError(`${response.status} ${response.statusText}`, response, error.resolvedResponseBody),
        );
    }
};

export const abortRetrying = async (response: Response): Promise<Response> => {
    const resolvedResponseBody = await response
        .json()
        .then(response => response)
        .catch(_ => {});
    throw new AbortError(
        createHttpResponseError(`${response.status} ${response.statusText}`, response, resolvedResponseBody),
    );
};

export const parseJSON = (response: Response): Response | Promise<any> =>
    response.status !== 204 ? response.json() : response;

export const parseSettings = ({
    method = RestApiMethodName.GET,
    data = null,
    headers = {},
    isMultipart = false,
    ...otherSettings
}: any = {}): RequestInit => {
    const mergedHeaders: any = {
        Accept: 'application/json',
        ...headers,
    };

    if (method !== RestApiMethodName.GET) {
        if (!isMultipart) {
            mergedHeaders['Content-Type'] = 'application/json';
        }
    }

    const getBody = (): BodyInit | null | undefined => {
        if (isMultipart) {
            return data;
        }
        return JSON.stringify(data);
    };

    return {
        body: data ? getBody() : null,
        method,
        ...mergeDeepLeft<any, any>(
            {
                headers: mergedHeaders,
            },
            otherSettings,
        ),
    };
};

export const parseEndpoint = (endpoint: string, params?: any): string => {
    const url = endpoint.indexOf('http') === 0 ? endpoint : config.apiUrl + endpoint;
    const querystring = params ? `?${queryString.stringify(params, { arrayFormat: 'bracket' })}` : '';
    return `${url}${querystring}`;
};
